import {
  MorphemeTilePresentationProvider,
  useMorphemeTilePresentation,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import { useMemo } from "react";
import { Platform, View } from "@-/com.npmjs.react-native";
import { cn } from "@-/com.npmjs.cn";
import {
  getMorphemeGlyphTilePinyinDotsGate,
  MorphemeGlyphTilePinyinDots,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui._components.pinyin-dots";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { toneForPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import { MorphemeTileGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyph-renderer";

/** What the tile cell draws as its front: the CJK glyph, or that cell's reading as diacritic pinyin. */
export type MorphemeGlyphTileCharacterFront = "glyph" | "pinyin";

/** One line of a pronunciation tile, placed so its ink exactly fills a box `inkHeight` tall. */
export type MorphemeGlyphTileInkLineStyle = {
  fontSize: number;
  /** The ink box: tone-mark top to baseline for the reading, the ideographic face for the glyph. */
  inkHeight: number;
  /** Offset of the text's line box from the ink box's top (negative when it starts above it). */
  lineBoxTop: number;
  lineHeight: number;
};

/** A pronunciation tile's reading and the glyph below it. */
export type MorphemeGlyphTilePronunciationStyle = {
  glyph: MorphemeGlyphTileInkLineStyle;
  pinyin: MorphemeGlyphTileInkLineStyle;
};

/**
 * Fraction of the glyph font size to translate the glyph downward so the CJK ink lands on the cell
 * center. A flex parent centers the Text's LINE BOX, but the ink does not sit at the line-box center:
 * Han Composite's CJK metrics are asymmetric (the baseline sits below the line-box center because the font
 * reserves more room above the baseline than below) and the ideograph rides above the baseline, so the
 * ink lands a couple px high and needs a downward nudge.
 *
 * Calibrated against the current Han Composite face using 一's single horizontal stroke. On the
 * 44pt Levels tile (fontSize 25 / lineHeight 28), the previous 2pt nudge left the native ink 2.5
 * physical pixels high at 3x. A 0.12 ratio gives a 3pt nudge, correcting that offset to half a pixel.
 * Because it is a fraction of the font size, the recentering scales with tile sizes. This
 * is a font-at-this-weight constant AND is coupled to the line-box headroom in the sizing module —
 * changing the font/weight or GLYPH_LINE_BOX_HEADROOM_RATIO shifts where the ink lands and needs
 * re-measuring (re-add a temporary `onTextLayout` log for 一, read the metrics, recompute). Android/web
 * text renderers lay the ink out already centered in the line box (measured separately), so no nudge.
 */
const GLYPH_INK_RECENTER_RATIO = Platform.OS === "android" || Platform.OS === "web" ? 0 : 0.12;

function MorphemeGlyphTileCharacterContent({
  additionalPinyinList,
  className = "size-full",
  erhua = false,
  front = "glyph",
  glyphStyle,
  grapheme,
  graphemeGlyph: graphemeGlyphOverride,
  pinyinDotsRowReserved = false,
  primaryPinyin = "",
  primaryPinyinStyle = null,
  uncolored = false,
}: {
  additionalPinyinList: readonly string[];
  className?: string;
  /**
   * Pinyin front only: the word's next character is an erhua 儿 folded into this cell, so its
   * reading ends in `r` (`diǎnr`) and keeps this syllable's tone color.
   */
  erhua?: boolean;
  /**
   * Curriculum word levels introduce vocabulary by audio, so their tiles draw each cell's reading
   * (`pinyin`) instead of the written glyph. Everywhere else keeps the default glyph front.
   */
  front?: MorphemeGlyphTileCharacterFront;
  glyphStyle: { fontSize: number; lineHeight: number } | null;
  grapheme: string;
  graphemeGlyph?: GraphemeGlyph;
  /** Keep a blank dots row so single-grapheme tiles size glyphs consistently. */
  pinyinDotsRowReserved?: boolean;
  primaryPinyin: string | null;
  /**
   * A pronunciation card shows its own reading above the written glyph, sized and placed by this
   * style (see `getMorphemeGlyphTilePronunciationStyle`).
   */
  primaryPinyinStyle?: MorphemeGlyphTilePronunciationStyle | null;
  /** Render the glyph in the theme foreground color, ignoring tone colors. */
  uncolored?: boolean;
}) {
  const {
    getGraphemeGlyph,
    getGraphemePrevalence,
    isToneColorsEnabled,
    svgTileGlyphsEnabled,
    toneColors,
    user,
  } = useMorphemeTilePresentation();
  const isCharacter = getGraphemePrevalence(grapheme).asCharacter !== "obscure";
  const studyPrimaryPinyin = isCharacter ? (primaryPinyin ?? "") : "";
  const dictGlyph = getGraphemeGlyph(grapheme);
  const primaryTone = toneForPinyin(studyPrimaryPinyin);
  const primaryColor =
    !isToneColorsEnabled || primaryTone === null ? null : toneColors[primaryTone];
  const glyph = useMemo(
    () => ({
      color: uncolored
        ? null
        : !isCharacter
          ? dictGlyph.color
          : studyPrimaryPinyin
            ? primaryColor
            : (graphemeGlyphOverride?.color ?? dictGlyph.color),
      display: graphemeGlyphOverride?.display ?? dictGlyph.display,
      fontWeight: dictGlyph.fontWeight,
      original: grapheme,
      render: getGraphemeGlyph(graphemeGlyphOverride?.display ?? dictGlyph.display).render,
    }),
    [
      dictGlyph,
      getGraphemeGlyph,
      grapheme,
      graphemeGlyphOverride,
      isCharacter,
      studyPrimaryPinyin,
      primaryColor,
      uncolored,
    ],
  );

  if (front === "pinyin") {
    const diacriticPinyin =
      studyPrimaryPinyin.length === 0
        ? ""
        : `${toDiacriticPinyin(studyPrimaryPinyin)}${erhua ? "r" : ""}`;
    const pinyinColor = uncolored ? null : primaryColor;
    return (
      <View className={cn(className ?? "size-full", "items-center justify-center px-0.5")}>
        <Text
          className={cn("w-full text-center text-foreground", glyphStyle === null && "opacity-0")}
          numberOfLines={1}
          style={
            pinyinColor === null ? (glyphStyle ?? undefined) : [glyphStyle, { color: pinyinColor }]
          }
        >
          {diacriticPinyin}
        </Text>
      </View>
    );
  }

  // The line box carries fixed headroom over the font size (so the ink never clips at top), and the
  // flex parent centers that box. On iOS the ink rides high inside the box, so nudge it down by a
  // fixed fraction of the font size to land the ink-center on the cell-center. Because both the
  // headroom and this nudge are fractions of the font size, the recentering holds at every tile size.
  const inkRecenterNudge =
    glyphStyle === null ? 0 : Math.round(glyphStyle.fontSize * GLYPH_INK_RECENTER_RATIO);
  const minLineHeightEm = Platform.select(glyph.render ?? {})?.minLineHeightEm;
  const renderedGlyphStyle =
    minLineHeightEm !== undefined && glyphStyle !== null
      ? {
          ...glyphStyle,
          lineHeight: Math.max(
            glyphStyle.lineHeight,
            Math.ceil(glyphStyle.fontSize * minLineHeightEm),
          ),
        }
      : glyphStyle;
  const graphemeFontGlyph = (
    <GraphemeFontGlyph
      className={cn("shrink text-center text-foreground", glyphStyle === null && "opacity-0")}
      glyph={glyph}
      hanziScript={user.hanziScript}
      style={
        inkRecenterNudge <= 0
          ? (renderedGlyphStyle ?? undefined)
          : [renderedGlyphStyle, { transform: [{ translateY: inkRecenterNudge }] }]
      }
    />
  );
  const renderedGlyph =
    svgTileGlyphsEnabled && glyphStyle !== null ? (
      <MorphemeTileGlyph fallback={graphemeFontGlyph} glyph={glyph} size={glyphStyle.fontSize} />
    ) : (
      graphemeFontGlyph
    );

  const pinyinDotsGate =
    !uncolored && isCharacter
      ? getMorphemeGlyphTilePinyinDotsGate({
          additionalPinyinList,
          reserved: pinyinDotsRowReserved,
        })
      : null;

  if (primaryPinyinStyle !== null && primaryPinyin) {
    // Each line sits in a box exactly as tall as its ink, and three equal flex gaps split the rest
    // of the tile: above the reading, between the reading's baseline and the glyph's top, and
    // below the glyph.
    const { glyph: glyphLine, pinyin: pinyinLine } = primaryPinyinStyle;
    const pronunciationGlyphStyle = {
      fontSize: glyphLine.fontSize,
      lineHeight: glyphLine.lineHeight,
    };
    const pronunciationFontGlyph = (
      <GraphemeFontGlyph
        className="text-center text-foreground"
        glyph={glyph}
        hanziScript={user.hanziScript}
        style={pronunciationGlyphStyle}
      />
    );
    return (
      <View className={cn(className ?? "size-full", "flex-col")}>
        <View className="flex-1" />
        <View style={{ height: pinyinLine.inkHeight }}>
          <Text
            className="absolute inset-x-0 px-0.5 text-center text-foreground"
            numberOfLines={1}
            style={[
              {
                fontSize: pinyinLine.fontSize,
                lineHeight: pinyinLine.lineHeight,
                top: pinyinLine.lineBoxTop,
              },
              glyph.color === null ? undefined : { color: glyph.color },
            ]}
          >
            {toDiacriticPinyin(primaryPinyin)}
          </Text>
        </View>
        <View className="flex-1" />
        <View style={{ height: glyphLine.inkHeight }}>
          {svgTileGlyphsEnabled ? (
            // The debug SVG's square is the em box, which shares the ideographic face's center.
            <View
              className="absolute inset-x-0 items-center"
              style={{ top: (glyphLine.inkHeight - glyphLine.fontSize) / 2 }}
            >
              <MorphemeTileGlyph
                fallback={pronunciationFontGlyph}
                glyph={glyph}
                size={glyphLine.fontSize}
              />
            </View>
          ) : (
            <View className="absolute inset-x-0 items-center" style={{ top: glyphLine.lineBoxTop }}>
              {pronunciationFontGlyph}
            </View>
          )}
        </View>
        <View className="flex-1" />
      </View>
    );
  }

  if (pinyinDotsRowReserved) {
    // Center the glyph in the FULL card (not the glyph-zone above the dots) so the whitespace above
    // the ink equals the whitespace below. The dots overlay the bottom band of that whitespace —
    // the glyph is sized (see `getMorphemeGlyphTileGraphemeGlyphStyle`) to reserve a dots-row of
    // margin on both sides, so the dots fit below without pushing the glyph upward.
    return (
      <View className={cn(className ?? "size-full", "relative items-center justify-center")}>
        {renderedGlyph}
        {pinyinDotsGate !== null && (
          <View className="absolute inset-x-0 bottom-0">
            <MorphemeGlyphTilePinyinDots gate={pinyinDotsGate} />
          </View>
        )}
      </View>
    );
  }

  return (
    <View className={cn("relative items-center justify-center", className ?? "size-full")}>
      {renderedGlyph}
      {pinyinDotsGate !== null && <MorphemeGlyphTilePinyinDots gate={pinyinDotsGate} />}
    </View>
  );
}

export function MorphemeGlyphTileCharacter(
  props: React.ComponentProps<typeof MorphemeGlyphTileCharacterContent>,
) {
  return (
    <MorphemeTilePresentationProvider>
      <MorphemeGlyphTileCharacterContent {...props} />
    </MorphemeTilePresentationProvider>
  );
}
